<template>
    <v-dialog
        :model-value="modelValue"
        max-width="640"
        scrollable
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card rounded="lg" class="corporate-dialog">
            <v-card-title class="corporate-dialog__header">
                <div>
                    <h2 class="corporate-dialog__title">{{ title }}</h2>
                    <p v-if="parentHint" class="corporate-dialog__subtitle">{{ parentHint }}</p>
                </div>
            </v-card-title>
            <v-card-text class="corporate-dialog__body">
                <section class="corporate-dialog__section">
                    <p class="corporate-dialog__section-label">{{ sectionDataLabel }}</p>
                    <div class="corporate-dialog__section-body">
                        <div class="corporate-dialog__row">
                            <v-select
                                v-model="form.type"
                                :items="typeOptions"
                                item-title="text"
                                item-value="value"
                                :label="labels.type"
                                variant="outlined"
                                density="compact"
                                :disabled="Boolean(form.id) || lockType"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <v-select
                                v-if="form.type === 'role'"
                                v-model="form.parentId"
                                :items="filteredParentOptions"
                                item-title="text"
                                item-value="value"
                                :label="labels.parent"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                        </div>
                        <v-text-field
                            v-model="form.name"
                            :label="labels.name"
                            :placeholder="namePlaceholder"
                            variant="outlined"
                            density="compact"
                            class="weeduc-admin-v-field"
                            hide-details="auto"
                        />
                        <v-textarea
                            v-model="form.description"
                            :label="labels.description"
                            variant="outlined"
                            density="compact"
                            rows="2"
                            class="weeduc-admin-v-field"
                            hide-details="auto"
                        />
                        <template v-if="showManager && form.type === 'sector'">
                            <v-autocomplete
                                v-model="form.managerUserId"
                                :items="managerOptions"
                                item-title="title"
                                item-value="value"
                                :label="labels.manager"
                                :placeholder="labels.managerSearch || t.structure.managerSearch"
                                variant="outlined"
                                density="compact"
                                clearable
                                :loading="managersLoading"
                                :no-data-text="managersLoading ? t.common.loading : (labels.noResponsibles || t.structure.noResponsibles)"
                                :menu-props="managerMenuProps"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            >
                                <template #item="{ props, item }">
                                    <v-list-item
                                        v-bind="props"
                                        :title="item.raw?.name || item.title"
                                        :subtitle="item.raw?.email || undefined"
                                    />
                                </template>
                            </v-autocomplete>
                            <p class="corporate-dialog__hint">{{ labels.managerHint }}</p>
                        </template>
                        <div class="corporate-dialog__row">
                            <v-select
                                v-model="form.status"
                                :items="statusOptions"
                                item-title="text"
                                item-value="value"
                                :label="labels.status"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                        </div>
                    </div>
                </section>
            </v-card-text>
            <v-card-actions class="corporate-dialog__actions">
                <v-spacer />
                <v-btn variant="text" class="text-none" @click="$emit('update:modelValue', false)">
                    {{ common.cancel }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    :loading="saving"
                    @click="submit"
                >
                    {{ common.save }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { corporateI18n } from '@/i18n/mixins';

export default {
    name: 'OrgUnitForm',
    mixins: [corporateI18n],
    props: {
        modelValue: Boolean,
        title: String,
        labels: Object,
        common: Object,
        initial: {
            type: Object,
            default: () => ({}),
        },
        parentOptions: {
            type: Array,
            default: () => [],
        },
        managerOptions: {
            type: Array,
            default: () => [],
        },
        managersLoading: {
            type: Boolean,
            default: false,
        },
        showManager: {
            type: Boolean,
            default: true,
        },
        lockType: Boolean,
        saving: Boolean,
    },
    emits: ['update:modelValue', 'submit', 'validation-error'],
    data() {
        return {
            form: this.defaultForm(),
        };
    },
    computed: {
        ...mapState(['dark']),
        typeOptions() {
            return [
                { text: this.labels.types.sector, value: 'sector' },
                { text: this.labels.types.role, value: 'role' },
            ];
        },
        statusOptions() {
            return [
                { text: this.labels.active, value: 'active' },
                { text: this.labels.inactive, value: 'inactive' },
            ];
        },
        parentHint() {
            if (this.form.type === 'sector') return this.labels.parentHintSector;
            if (this.form.type === 'role') return this.labels.parentHintRole;
            return '';
        },
        namePlaceholder() {
            if (this.form.type === 'sector') return this.labels.namePlaceholderSector;
            if (this.form.type === 'role') return this.labels.namePlaceholderRole;
            return '';
        },
        managerMenuProps() {
            return {
                maxHeight: 320,
                contentClass: this.dark ? 'weeduc-admin-select-menu-dark' : undefined,
            };
        },
        companyParentId() {
            const company = (this.parentOptions || []).find((o) => o.unitType === 'company');
            return company?.value || null;
        },
        filteredParentOptions() {
            const all = this.parentOptions || [];
            if (this.form.type === 'role') {
                return all.filter((o) => o.unitType === 'sector');
            }
            return [];
        },
        sectionDataLabel() {
            return this.common?.sectionData || this.t.common.sectionData;
        },
    },
    watch: {
        modelValue(open) {
            if (open) {
                this.syncForm(this.initial);
            }
        },
        initial: {
            deep: true,
            handler(value) {
                if (this.modelValue) {
                    this.syncForm(value);
                }
            },
        },
        'form.type'() {
            if (this.form.id) return;
            this.applyParentDefaults();
            if (this.form.type !== 'sector') {
                this.form.managerUserId = null;
            }
        },
    },
    methods: {
        defaultForm() {
            return {
                id: null,
                type: 'sector',
                parentId: null,
                name: '',
                description: '',
                managerUserId: null,
                status: 'active',
            };
        },
        syncForm(value) {
            this.form = { ...this.defaultForm(), ...value };
            if (this.form.managerUserId != null && this.form.managerUserId !== '') {
                this.form.managerUserId = String(this.form.managerUserId);
            } else {
                this.form.managerUserId = null;
            }
            this.applyParentDefaults();
        },
        applyParentDefaults() {
            if (this.form.type === 'sector') {
                this.form.parentId = this.companyParentId || this.form.parentId;
                return;
            }
            if (this.form.type === 'role') {
                const allowed = this.filteredParentOptions.map((o) => o.value);
                if (!this.form.parentId || !allowed.includes(this.form.parentId)) {
                    this.form.parentId = allowed[0] || null;
                }
            }
        },
        submit() {
            if (!this.form.name?.trim()) {
                this.$emit('validation-error', this.labels.nameRequired || this.t.structure.nameRequired);
                return;
            }
            const payload = { ...this.form };
            if (payload.type === 'sector') {
                // parentId null → backend resolve para a raiz da empresa
                payload.parentId = this.companyParentId || payload.parentId || null;
            }
            if (payload.type === 'role' && !payload.parentId) {
                this.$emit(
                    'validation-error',
                    this.labels.parentRequiredRole || this.t.structure.parentRequiredRole,
                );
                return;
            }
            this.$emit('submit', payload);
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-org-form__warn {
    margin: 0;
    font-size: 0.78rem;
    color: #c62828;
}
</style>
